import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  engineeringBacklogApi,
  type BacklogDeliverable,
  type DependencyMatrixEntry,
  type EBPDashboard,
  type EBPBibleStatus,
  type EngineeringTask,
  type Epic,
  type HierarchyNode,
  type Milestone,
  type ProductionChecklistItem,
  type RiskRegisterEntry,
  type SprintDefinitionOfDoneItem,
  type SprintPhilosophyItem,
  type SprintPlan,
  type StoryValidation,
  type UserStory,
} from '../../api/engineering-backlog-sprint-planning';

type Tab = 'overview' | 'hierarchy' | 'epics' | 'stories' | 'tasks' | 'sprints' | 'dependencies' | 'milestones' | 'risks' | 'status';
type Theme = 'light' | 'dark';

const priorityColors: Record<string, string> = {
  p0: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
  p1: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  p2: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};

const impactColors: Record<string, string> = {
  high: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  low: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
};

export function EngineeringBacklogSprintPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<EBPDashboard | null>(null);
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [philosophy, setPhilosophy] = useState<SprintPhilosophyItem[]>([]);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [stories, setStories] = useState<UserStory[]>([]);
  const [tasks, setTasks] = useState<EngineeringTask[]>([]);
  const [sprints, setSprints] = useState<SprintPlan[]>([]);
  const [dependencies, setDependencies] = useState<DependencyMatrixEntry[]>([]);
  const [dod, setDod] = useState<SprintDefinitionOfDoneItem[]>([]);
  const [deliverables, setDeliverables] = useState<BacklogDeliverable[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [risks, setRisks] = useState<RiskRegisterEntry[]>([]);
  const [checklist, setChecklist] = useState<ProductionChecklistItem[]>([]);
  const [status, setStatus] = useState<EBPBibleStatus | null>(null);
  const [validation, setValidation] = useState<StoryValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, hier, phil, ep, st, tk, sp, deps, dodItems, del, ms, rk, cl, stt] = await Promise.all([
        engineeringBacklogApi.dashboard(),
        engineeringBacklogApi.hierarchy(),
        engineeringBacklogApi.sprintPhilosophy(),
        engineeringBacklogApi.epics(),
        engineeringBacklogApi.userStories(),
        engineeringBacklogApi.engineeringTasks(),
        engineeringBacklogApi.sprintPlans(),
        engineeringBacklogApi.dependencyMatrix(),
        engineeringBacklogApi.definitionOfDone(),
        engineeringBacklogApi.deliverables(),
        engineeringBacklogApi.milestones(),
        engineeringBacklogApi.riskRegister(),
        engineeringBacklogApi.productionChecklist(),
        engineeringBacklogApi.status(),
      ]);
      setDashboard(dash);
      setHierarchy(hier);
      setPhilosophy(phil);
      setEpics(ep);
      setStories(st);
      setTasks(tk);
      setSprints(sp);
      setDependencies(deps);
      setDod(dodItems);
      setDeliverables(del);
      setMilestones(ms);
      setRisks(rk);
      setChecklist(cl);
      setStatus(stt);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load engineering backlog');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateStory = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await engineeringBacklogApi.validateStory({
        story_key: 'auth_login',
        attrs: {
          business_goal_defined: true, persona_defined: true, acceptance_criteria: true,
          success_kpis: true, api_requirements: true, database_requirements: true,
          ui_components: true, security_requirements: true, test_cases: true,
        },
      });
      setValidation(result);
      setActionMsg(result.valid ? 'Story passes EBP-000001 definition of done' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-violet-900/40 bg-slate-900' : 'border-violet-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-violet-50/20 text-slate-900';
  const accent = dark ? 'text-violet-300' : 'text-violet-700';
  const tabActive = 'bg-violet-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'hierarchy', label: 'Hierarchy' },
    { id: 'epics', label: 'Epics' },
    { id: 'stories', label: 'User Stories' },
    { id: 'tasks', label: 'Tasks' },
    { id: 'sprints', label: 'Sprint Plans' },
    { id: 'dependencies', label: 'Dependencies' },
    { id: 'milestones', label: 'Milestones & Risks' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>EBP-000001</p>
          <h1 className="text-2xl font-semibold">Engineering Backlog & Sprint Planning</h1>
          <p className={`text-sm ${muted}`}>
            Complete engineering backlog, epic breakdown, user stories, sprint planning, dependency mapping and production execution roadmap
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/marketplace-capability-architecture" className={`${accent} hover:underline`}>MCA-000001</Link>
            <span className={muted}>→</span>
            <Link to="/platform/engineering-execution-bible" className={`${accent} hover:underline`}>EEB-000001</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Executable Engineering Backlog</h2>
            <p className={`mt-1 text-sm ${muted}`}>Depends on: {dashboard.depends_on.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              Every engineering task maps to business capability, KPI, DNA, AI, UI, API, database, testing, and deployment.
              No engineering work exists without business value.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Epics', value: dashboard.epics },
              { label: 'User Stories', value: dashboard.user_stories },
              { label: 'Engineering Tasks', value: dashboard.engineering_tasks },
              { label: 'Sprint Plans', value: dashboard.sprint_plans },
              { label: 'Dependencies', value: dashboard.dependency_entries },
              { label: 'Milestones', value: dashboard.milestones },
              { label: 'Risk Register', value: dashboard.risk_register },
              { label: 'Production Checklist', value: dashboard.production_checklist },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Sprint Philosophy</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {philosophy.map((p) => (
                <div key={p.key} className="rounded-lg border border-violet-200 p-3 dark:border-violet-800">
                  <span className="font-medium">{p.name}</span>
                  <p className={`mt-1 text-xs ${muted}`}>{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'hierarchy' && (
        <div className="space-y-2">
          {hierarchy.map((n, i) => (
            <div key={n.key} className={`flex items-center gap-4 rounded-xl border p-4 ${card}`} style={{ marginLeft: `${(n.level - 1) * 16}px` }}>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${tabActive}`}>L{n.level}</span>
              <div>
                <h3 className="font-semibold">{n.name}</h3>
                {n.description && <p className={`text-sm ${muted}`}>{n.description}</p>}
              </div>
              {i < hierarchy.length - 1 && <span className={`ml-auto text-xs ${muted}`}>↓</span>}
            </div>
          ))}
        </div>
      )}

      {tab === 'epics' && (
        <div className="grid gap-3 md:grid-cols-2">
          {epics.map((e) => (
            <div key={e.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold">{e.name}</h3>
                <span className={`shrink-0 rounded px-2 py-0.5 text-xs ${priorityColors[e.priority] ?? ''}`}>{e.priority.toUpperCase()}</span>
              </div>
              <p className={`mt-2 text-sm ${muted}`}>{e.business_value}</p>
              <p className={`mt-1 text-xs ${muted}`}>Capability: {e.capability} · Sprint: {e.sprint_target ?? '—'}</p>
              {e.kpis && e.kpis.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {e.kpis.map((k) => <span key={k} className="rounded bg-violet-100 px-2 py-0.5 text-xs dark:bg-violet-900">{k}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'stories' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead>
              <tr className={`border-b text-left ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                <th className="px-4 py-2">Story</th>
                <th className="px-4 py-2">Epic</th>
                <th className="px-4 py-2">Persona</th>
                <th className="px-4 py-2">Sprint</th>
                <th className="px-4 py-2">Points</th>
              </tr>
            </thead>
            <tbody>
              {stories.map((s) => (
                <tr key={s.key} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2 font-medium">{s.title}</td>
                  <td className={`px-4 py-2 font-mono text-xs ${muted}`}>{s.epic_key}</td>
                  <td className={`px-4 py-2 ${muted}`}>{s.persona}</td>
                  <td className={`px-4 py-2 ${muted}`}>{s.sprint ?? '—'}</td>
                  <td className="px-4 py-2">{s.story_points ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'tasks' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead>
              <tr className={`border-b text-left ${dark ? 'border-slate-800' : 'border-slate-200'}`}>
                <th className="px-4 py-2">Task</th>
                <th className="px-4 py-2">Story</th>
                <th className="px-4 py-2">Complexity</th>
                <th className="px-4 py-2">API</th>
                <th className="px-4 py-2">UI</th>
                <th className="px-4 py-2">DB</th>
                <th className="px-4 py-2">Tests</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.key} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2 font-medium">{t.title}</td>
                  <td className={`px-4 py-2 font-mono text-xs ${muted}`}>{t.story_key}</td>
                  <td className="px-4 py-2 uppercase">{t.complexity}</td>
                  <td className="px-4 py-2">{t.has_api ? '✓' : '—'}</td>
                  <td className="px-4 py-2">{t.has_frontend ? '✓' : '—'}</td>
                  <td className="px-4 py-2">{t.has_database ? '✓' : '—'}</td>
                  <td className="px-4 py-2">{t.has_tests ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'sprints' && (
        <div className="space-y-4">
          {sprints.map((sp) => (
            <div key={sp.key} className={`rounded-xl border p-5 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{sp.name}</h3>
                <span className={`text-xs ${muted}`}>{sp.duration}</span>
              </div>
              <p className={`mt-2 text-sm ${muted}`}>{sp.goal}</p>
              {sp.epics && sp.epics.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {sp.epics.map((e) => <span key={e} className="rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">{e}</span>)}
                </div>
              )}
              {sp.deliverables && sp.deliverables.length > 0 && (
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {sp.deliverables.map((d) => <li key={d}>{d}</li>)}
                </ul>
              )}
            </div>
          ))}
          <section>
            <h2 className="mb-3 text-lg font-semibold">Sprint Definition of Done</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {dod.map((d) => (
                <div key={d.key} className={`rounded-lg border px-4 py-2 ${card}`}>
                  <span className="font-medium">{d.name}</span>
                  <span className={`ml-2 text-xs ${muted}`}>({d.category})</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'dependencies' && (
        <div className="space-y-3">
          {dependencies.map((d) => (
            <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{d.source}</h3>
                {d.critical_path && <span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800 dark:bg-red-900 dark:text-red-200">Critical Path</span>}
              </div>
              <p className={`mt-1 text-sm ${muted}`}>Depends on: {d.depends_on.join(', ')}</p>
            </div>
          ))}
          <section>
            <h2 className="mb-3 text-lg font-semibold">Deliverables</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {deliverables.map((d) => (
                <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{d.name}</h3>
                    <span className={`rounded px-2 py-0.5 text-xs ${d.status === 'complete' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'}`}>
                      {d.status}
                    </span>
                  </div>
                  {d.description && <p className={`mt-1 text-sm ${muted}`}>{d.description}</p>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'milestones' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Release Milestones</h2>
            <div className="space-y-3">
              {milestones.map((m) => (
                <div key={m.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{m.name}</h3>
                    <span className={`text-xs ${muted}`}>{m.target_date}</span>
                  </div>
                  <p className={`text-sm ${muted}`}>{m.description}</p>
                  <p className={`mt-1 text-xs ${muted}`}>{m.sprint}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Risk Register</h2>
            <div className="space-y-3">
              {risks.map((r) => (
                <div key={r.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{r.risk}</h3>
                    <span className={`rounded px-2 py-0.5 text-xs ${impactColors[r.impact] ?? ''}`}>{r.impact}</span>
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>Mitigation: {r.mitigation}</p>
                  {r.owner && <p className={`text-xs ${muted}`}>Owner: {r.owner}</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Production Checklist</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {checklist.map((c) => (
                <div key={c.key} className={`flex items-center justify-between rounded-lg border px-4 py-2 ${card}`}>
                  <span className="text-sm">{c.item}</span>
                  <span className={`rounded px-2 py-0.5 text-xs ${c.status === 'complete' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}>
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">{status.status}</span>
            {status.business_value_mapped && <p className={`mt-3 text-sm ${muted}`}>Every task mapped to business value</p>}
            {status.execution_ready && <p className={`text-sm ${muted}`}>Complete engineering execution plan ready</p>}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate User Story</h3>
            <button type="button" disabled={busy} onClick={validateStory} className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}>
              {busy ? 'Validating…' : 'Run Sample Validation'}
            </button>
            {validation && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">{JSON.stringify(validation, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
